import React from "react";
import {
  Box,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { CURRENCIES, type TradesAccordionProps } from "./types";

export const TradesAccordion: React.FC<TradesAccordionProps> = ({
  expanded,
  onAccordionChange,
  tradeRows,
  clients,
  custodians,
  instruments,
  handleTradeChange,
  handleAddTradeRow,
  handleRemoveTradeRow,
  handleReAddTradeRow,
}) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={onAccordionChange}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TrendingUpIcon color="primary" />
          <Typography fontWeight={500}>Add Trades / Subscriptions</Typography>
          <Typography variant="body2" color="text.secondary">
            (Securities & Funds)
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Transaction Type</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Custodian</TableCell>
                <TableCell>Instrument/ISIN</TableCell>
                <TableCell>Side</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell width={100}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tradeRows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.transaction_type}
                      onChange={(e) => handleTradeChange(row.id, "transaction_type", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="buy">Buy</MenuItem>
                      <MenuItem value="sell">Sell</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.client_id}
                      onChange={(e) => handleTradeChange(row.id, "client_id", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {clients.map((c) => (
                        <MenuItem key={c.client_id} value={c.client_id}>
                          {c.client_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.custodian_id}
                      onChange={(e) => handleTradeChange(row.id, "custodian_id", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {custodians.map((c) => (
                        <MenuItem key={c.custodian_id} value={c.custodian_id}>
                          {c.custodian_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.instrument_isin}
                      onChange={(e) => handleTradeChange(row.id, "instrument_isin", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      {instruments.map((i) => (
                        <MenuItem key={i.isin || i.symbol} value={i.isin || ""}>
                          {i.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.side}
                      onChange={(e) => handleTradeChange(row.id, "side", e.target.value)}
                      variant="standard"
                      sx={{ minWidth: 80 }}
                    >
                      <MenuItem value="buy">Buy</MenuItem>
                      <MenuItem value="sell">Sell</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={row.amount || ""}
                      onChange={(e) => handleTradeChange(row.id, "amount", parseFloat(e.target.value) || 0)}
                      variant="standard"
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.currency}
                      onChange={(e) => handleTradeChange(row.id, "currency", e.target.value)}
                      variant="standard"
                      sx={{ width: 80 }}
                    >
                      {CURRENCIES.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {index === tradeRows.length - 1 ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddTradeRow}
                          sx={{ minWidth: 60 }}
                        >
                          ADD
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleReAddTradeRow(row)}
                            sx={{ minWidth: 60 }}
                          >
                            ADD
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTradeRow(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};
